import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Building2, 
  Palette, 
  Globe, 
  Phone, 
  MapPin, 
  Save,
  Image,
  Instagram,
  Facebook,
  Linkedin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { settingsAPI } from '@/lib/api';

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const Settings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    company_name: '',
    tagline: '',
    logo_url: '',
    primary_color: '#0F4C81',
    secondary_color: '#00A3C4',
    website: '',
    phone: '',
    address: '',
    instagram: '',
    facebook: '',
    tiktok: '',
    linkedin_company: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await settingsAPI.update(settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Company Settings</h1>
        <p className="text-slate-600 mt-1">
          Configure your company branding and contact information
        </p>
      </div>

      <motion.form
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={handleSubmit}
        className="space-y-6"
      >
        {/* Company Info */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-[#0F4C81]/10 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#0F4C81]" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">Company Information</h2>
              <p className="text-sm text-slate-500">Basic company details shown on cards</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="form-group">
              <Label htmlFor="company_name" className="form-label">
                Company Name
              </Label>
              <Input
                id="company_name"
                value={settings.company_name}
                onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                placeholder="T.C. Christoforou Ltd"
                data-testid="input-company-name"
              />
            </div>
            <div className="form-group">
              <Label htmlFor="tagline" className="form-label">
                Tagline
              </Label>
              <Input
                id="tagline"
                value={settings.tagline}
                onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                placeholder="Serving Health Since 1978"
                data-testid="input-tagline"
              />
            </div>
          </div>

          <div className="form-group">
            <Label htmlFor="logo_url" className="form-label">
              <Image className="w-4 h-4 inline mr-2" />
              Logo URL
            </Label>
            <Input
              id="logo_url"
              value={settings.logo_url}
              onChange={(e) => setSettings({ ...settings, logo_url: e.target.value })}
              placeholder="https://www.example.com/logo.png"
              data-testid="input-logo-url"
            />
            {settings.logo_url && (
              <div className="mt-3 p-4 bg-slate-50 rounded-xl">
                <p className="text-xs text-slate-500 mb-2">Logo Preview:</p>
                <img 
                  src={settings.logo_url} 
                  alt="Company Logo" 
                  className="h-10 object-contain"
                  onError={(e) => e.target.style.display = 'none'}
                />
              </div>
            )}
          </div>
        </div>

        {/* Brand Colors */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-[#00A3C4]/10 flex items-center justify-center">
              <Palette className="w-5 h-5 text-[#00A3C4]" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">Brand Colors</h2>
              <p className="text-sm text-slate-500">Customize your card appearance</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="form-group">
              <Label htmlFor="primary_color" className="form-label">
                Primary Color
              </Label>
              <div className="flex gap-3">
                <div className="relative">
                  <input
                    type="color"
                    id="primary_color"
                    value={settings.primary_color}
                    onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                    className="w-12 h-12 rounded-xl cursor-pointer border border-slate-200"
                  />
                </div>
                <Input
                  value={settings.primary_color}
                  onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                  placeholder="#0F4C81"
                  className="flex-1 font-mono"
                  data-testid="input-primary-color"
                />
              </div>
            </div>
            <div className="form-group">
              <Label htmlFor="secondary_color" className="form-label">
                Secondary Color
              </Label>
              <div className="flex gap-3">
                <div className="relative">
                  <input
                    type="color"
                    id="secondary_color"
                    value={settings.secondary_color}
                    onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                    className="w-12 h-12 rounded-xl cursor-pointer border border-slate-200"
                  />
                </div>
                <Input
                  value={settings.secondary_color}
                  onChange={(e) => setSettings({ ...settings, secondary_color: e.target.value })}
                  placeholder="#00A3C4"
                  className="flex-1 font-mono"
                  data-testid="input-secondary-color"
                />
              </div>
            </div>
          </div>

          {/* Color Preview */}
          <div className="p-4 rounded-xl bg-slate-50">
            <p className="text-xs text-slate-500 mb-3">Card Header Preview:</p>
            <div 
              className="h-20 rounded-lg"
              style={{ 
                background: `linear-gradient(135deg, ${settings.primary_color} 0%, ${settings.secondary_color} 100%)` 
              }}
            />
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
              <Globe className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">Default Contact Info</h2>
              <p className="text-sm text-slate-500">Fallback info when employee fields are empty</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="form-group">
              <Label htmlFor="website" className="form-label">
                <Globe className="w-4 h-4 inline mr-2" />
                Website
              </Label>
              <Input
                id="website"
                value={settings.website}
                onChange={(e) => setSettings({ ...settings, website: e.target.value })}
                placeholder="https://www.tcc.com.cy"
                data-testid="input-website"
              />
            </div>
            <div className="form-group">
              <Label htmlFor="phone" className="form-label">
                <Phone className="w-4 h-4 inline mr-2" />
                Phone
              </Label>
              <Input
                id="phone"
                value={settings.phone}
                onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                placeholder="+357 24668899"
                data-testid="input-phone"
              />
            </div>
          </div>

          <div className="form-group">
            <Label htmlFor="address" className="form-label">
              <MapPin className="w-4 h-4 inline mr-2" />
              Address
            </Label>
            <Input
              id="address"
              value={settings.address}
              onChange={(e) => setSettings({ ...settings, address: e.target.value })}
              placeholder="12 Klimis, Aradippou Industrial Area, 7101 Aradippou, Cyprus"
              data-testid="input-address"
            />
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center">
              <Instagram className="w-5 h-5 text-pink-600" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">Social Media Links</h2>
              <p className="text-sm text-slate-500">Company social media profiles shown on cards</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="form-group">
              <Label htmlFor="instagram" className="form-label">
                <Instagram className="w-4 h-4 inline mr-2" />
                Instagram
              </Label>
              <Input
                id="instagram"
                value={settings.instagram || ''}
                onChange={(e) => setSettings({ ...settings, instagram: e.target.value })}
                placeholder="https://www.instagram.com/yourcompany"
                data-testid="input-instagram"
              />
            </div>
            <div className="form-group">
              <Label htmlFor="facebook" className="form-label">
                <Facebook className="w-4 h-4 inline mr-2" />
                Facebook
              </Label>
              <Input
                id="facebook"
                value={settings.facebook || ''}
                onChange={(e) => setSettings({ ...settings, facebook: e.target.value })}
                placeholder="https://www.facebook.com/yourcompany"
                data-testid="input-facebook"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="form-group">
              <Label htmlFor="tiktok" className="form-label">
                <TikTokIcon className="w-4 h-4 inline mr-2" />
                TikTok
              </Label>
              <Input
                id="tiktok"
                value={settings.tiktok || ''}
                onChange={(e) => setSettings({ ...settings, tiktok: e.target.value })}
                placeholder="https://www.tiktok.com/@yourcompany"
                data-testid="input-tiktok"
              />
            </div>
            <div className="form-group">
              <Label htmlFor="linkedin_company" className="form-label">
                <Linkedin className="w-4 h-4 inline mr-2" />
                LinkedIn (Company)
              </Label>
              <Input
                id="linkedin_company"
                value={settings.linkedin_company || ''}
                onChange={(e) => setSettings({ ...settings, linkedin_company: e.target.value })}
                placeholder="https://www.linkedin.com/company/yourcompany"
                data-testid="input-linkedin-company"
              />
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            type="submit" 
            className="btn-primary"
            disabled={saving}
            data-testid="save-settings-btn"
          >
            {saving && <div className="spinner mr-2" />}
            <Save className="w-4 h-4 mr-2" />
            Save Settings
          </Button>
        </div>
      </motion.form>
    </div>
  );
};

export default Settings;
